// eslint-disable-next-line no-restricted-imports
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import type { FC, FormEvent } from "react";

import { GeneralSettingsForm } from "./GeneralSettingsForm";
import { GradeForm } from "./GradeForm";
import { SectionInfoForm } from "./SectionInfoForm";
import { VirtualClassForm } from "./VirtualClassForm";
import { TAB_INDEX } from "./types";
import { useSectionSettings } from "./use-section-settings";

import type { SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AssessPointsMarkerIcon, BookSectionIcon, GearSettingsIcon, VideoIcon } from "mds/icons";
import { useSectionSettingActions } from "utils/section";
import { isAVEnabled } from "utils/waffle-flags";

type SettingsTabsProps = {
  section: SectionType;
  tabIndex: number;
  canAuthorCourse: boolean;
  onClose: () => void;
  setTabIndex: (index: number) => void;
};

export const SettingsTabs: FC<SettingsTabsProps> = ({
  section,
  tabIndex,
  canAuthorCourse,
  onClose,
  setTabIndex,
}) => {
  const { handleSectionDelete } = useSectionSettingActions();

  const {
    generalForm,
    sectionInfoForm,
    gradeForm,
    virtualClassForm,
    isAnyDirty,
    isAllValid,
    isLinked,
    isArchived,
    saveAll,
  } = useSectionSettings({
    section,
    canAuthorCourse,
    onClose,
    setTabIndex,
  });

  const disabled = isLinked || isArchived;

  const tabClassName =
    "group/tab -mb-0.5 border-b-2 border-blue-tint-90 py-3 text-black-tint-20 hover:border-blue-tint-70 data-[selected]:border-blue";

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (disabled) {
      return;
    }

    event.preventDefault();
    saveAll();
  };

  const onHandleSectionDelete = () => {
    onClose();
    handleSectionDelete(section);
  };

  const showDeleteSection = tabIndex === TAB_INDEX.sectionInfo;

  return (
    <form onSubmit={onSubmit}>
      <TabGroup className="mt-3" selectedIndex={tabIndex} onChange={setTabIndex}>
        <TabList className="flex flex-row gap-8 border-b-2 border-blue-tint-90 text-sm font-medium">
          <Tab className={tabClassName}>
            <IconText
              iconStart={<GearSettingsIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("settings.general")}
            />
          </Tab>

          <Tab className={tabClassName}>
            <IconText
              iconStart={<BookSectionIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("glossary.section_info")}
            />
          </Tab>

          <Tab className={tabClassName}>
            <IconText
              iconStart={<AssessPointsMarkerIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("settings.section_score")}
            />
          </Tab>

          {isAVEnabled() && (
            <Tab className={tabClassName}>
              <IconText
                iconStart={<VideoIcon className="group-data-[selected]/tab:text-blue" />}
                text={t("glossary.virtual_class")}
              />
            </Tab>
          )}
        </TabList>

        <TabPanels>
          <TabPanel>
            <GeneralSettingsForm className="pt-5" form={generalForm} />
          </TabPanel>

          <TabPanel>
            <SectionInfoForm className="pt-5" form={sectionInfoForm} onClose={onClose} />
          </TabPanel>

          <TabPanel>
            <GradeForm className="pt-5" form={gradeForm} />
          </TabPanel>

          {isAVEnabled() && (
            <TabPanel>
              <VirtualClassForm className="pt-5" form={virtualClassForm} />
            </TabPanel>
          )}
        </TabPanels>
      </TabGroup>

      <div className="mt-8 flex items-center justify-between gap-2">
        <div>
          {showDeleteSection && (
            <Tooltip content={!canAuthorCourse ? t("settings.delete_section_admins_only") : ""}>
              <SquareButton
                // Archived sections are read-only for settings, but deleting is still allowed.
                disabled={!canAuthorCourse || isLinked}
                kind="caution"
                size="m"
                type="button"
                onClick={onHandleSectionDelete}
              >
                {t("settings.delete_section")}
              </SquareButton>
            </Tooltip>
          )}
        </div>

        {/* flex-row-reverse so screen readers encounter Save before Cancel */}
        <div className="flex flex-row-reverse justify-start gap-2">
          <SquareButton
            disabled={disabled || !isAnyDirty || !isAllValid}
            eventName="Section Settings - Save changes"
            kind="accent"
            size="m"
            title={t("common.save_changes")}
            type="submit"
          >
            {t("common.save_changes")}
          </SquareButton>

          <SquareButton
            kind="tertiary"
            size="m"
            title={t("common.cancel")}
            type="button"
            onClick={onClose}
          >
            {t("common.cancel")}
          </SquareButton>
        </div>
      </div>
    </form>
  );
};
